"use client";

import { useCallback, useEffect, useRef } from "react";

export const focusableSelectors = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(", ");

export const useFocusTrap = (isActive: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleGetFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];

    const candidates = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(focusableSelectors),
    );
    // Filter out hidden elements and those with display: none
    return candidates.filter((el) => {
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden" && el.offsetParent !== null;
    });
  }, []);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Tab") return;

    const focusableElements = handleGetFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const activeElement = document.activeElement;
    const lastElement = focusableElements[focusableElements.length - 1];

    event.preventDefault();

    if (!activeElement) {
      firstElement.focus();
      return;
    }

    const focusIndex = focusableElements.indexOf(activeElement as HTMLElement);
    if (event.shiftKey) {
      // Shift + Tab
      if (focusIndex <= 0) lastElement.focus();
      else focusableElements[focusIndex - 1].focus();
    } else {
      // Tab
      if (focusIndex === focusableElements.length - 1) firstElement.focus();
      else focusableElements[focusIndex + 1].focus();
    }
  };

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    document.addEventListener("keydown", handleKeyDown);

    // Focus first element on mount.
    // const focusableElements = handleGetFocusableElements();
    // if (focusableElements.length > 0) focusableElements[0].focus();
    // else containerRef.current.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive]);

  return containerRef;
};
